import { useQuery } from "@tanstack/react-query";
import { useContext, useRef } from "react";
import ComplianceConnectService from "@app/compliance-connect/common/complianceConnectService";
import { ComplianceConnectCountySpecialties } from "@app/compliance-connect/regulator/complianceConnectTypes";
import { ComplianceConnectFiltersContext } from "@app/compliance-connect/regulator/contexts/ComplianceConnectFiltersContext";
import { UserContext } from "@app/user/UserContext";

interface UseTopFiveCountySpecialtiesResult {
    topFiveCountySpecialties: ComplianceConnectCountySpecialties[] | null;
    topFiveCountySpecialtiesError: boolean;
    isLoading: boolean;
}

export function useTopFiveCountySpecialties(): UseTopFiveCountySpecialtiesResult {
    const service = useRef(new ComplianceConnectService()).current;
    const { apiReadyFilters, isFilterSelectionEmpty } = useContext(ComplianceConnectFiltersContext);
    const user = useContext(UserContext);

    const { data, error, isPending } = useQuery({
        enabled: apiReadyFilters != null && !isFilterSelectionEmpty && user?.userId != null && user?.clientId != null,
        queryFn: ({ signal }) => service.getTopFiveCountySpecialties({
            networkNameSearch: apiReadyFilters.networkNameSearch || undefined,
            serviceAreaIds: apiReadyFilters.serviceAreaIds.length ? apiReadyFilters.serviceAreaIds : undefined,
            specialtyGroupCodes: apiReadyFilters.specialtyGroupCodes.length ? apiReadyFilters.specialtyGroupCodes : undefined,
        }, signal),
        queryKey: ["cc-top-five-county-specialties", user?.userId, user?.clientId, JSON.stringify(apiReadyFilters)],
        staleTime: 5 * 60 * 1000,
    });

    // An explicit "select nothing" is not sendable as an empty-array request (the backend can't tell
    // it apart from "select everything" — see ComplianceConnectFiltersContext's applyFilters comment),
    // so short-circuit client-side instead of firing a query that would come back as "match everything".
    if (isFilterSelectionEmpty) {
        return { isLoading: false, topFiveCountySpecialties: [], topFiveCountySpecialtiesError: false };
    }

    return {
        isLoading: isPending,
        topFiveCountySpecialties: data ?? null,
        topFiveCountySpecialtiesError: error != null,
    };
}
